import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import ActionBar from '../components/ActionBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CustomCalendar from '../components/CustomCalendar';
import { Skeleton } from '../components/ui/skeleton';
import { Users, Maximize2, ChevronLeft, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { saveSearch, loadSearch, toFutureDate } from '../lib/searchState';

export default function RoomDetailPage() {
  const { id } = useParams();
  const { t, language, formatPrice, BACKEND_URL, getImageUrl } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const saved = loadSearch();
  const initChildren = () => {
    const fromUrl = searchParams.get('children');
    if (fromUrl !== null) return fromUrl.split(',').filter(s => s !== '').map(Number).filter(n => !isNaN(n) && n >= 0);
    return Array.isArray(saved?.children) ? saved.children : [];
  };
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkIn, setCheckIn] = useState(toFutureDate(searchParams.get('check_in') || saved?.checkIn));
  const [checkOut, setCheckOut] = useState(toFutureDate(searchParams.get('check_out') || saved?.checkOut));
  const [guests, setGuests] = useState(parseInt(searchParams.get('guests')) || saved?.guests || 2);
  const [childAges, setChildAges] = useState(initChildren);
  const [priceInfo, setPriceInfo] = useState(null);
  const [activeDiscounts, setActiveDiscounts] = useState([]);
  const [priceLoading, setPriceLoading] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [activeField, setActiveField] = useState(null); // 'checkin' | 'checkout' | 'guests'

  const locale = language === 'tr' ? tr : enUS;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Keyboard navigation for gallery
  useEffect(() => {
    if (!galleryOpen) return;
    
    const handleKeyDown = (e) => {
      const images = room?.images || [];
      if (images.length <= 1) return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setGalleryIdx((prev) => (prev - 1 + images.length) % images.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setGalleryIdx((prev) => (prev + 1) % images.length);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setGalleryOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, room?.images]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/rooms/${id}`)
      .then(res => { setRoom(res.data); setLoading(false); })
      .catch(() => { setLoading(false); navigate('/rooms'); });
  }, [id, BACKEND_URL, navigate]);

  // Clamp guests/children to room limits once room loads
  useEffect(() => {
    if (!room) return;
    if (guests > (room.capacity || 2)) setGuests(room.capacity || 2);
    const maxCh = room.child_policy?.max_children ?? 2;
    if (childAges.length > maxCh) setChildAges(prev => prev.slice(0, maxCh));
  }, [room]);

  // Persist selections across pages
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/discounts/active`)
      .then(res => setActiveDiscounts(res.data))
      .catch(() => {});
  }, [BACKEND_URL]);

  const detailDiscount = (() => {
    const refStart = checkIn ? format(checkIn, 'yyyy-MM-dd') : new Date().toISOString().slice(0, 10);
    const refEnd = checkOut ? format(checkOut, 'yyyy-MM-dd') : refStart;
    let best = null;
    for (const d of activeDiscounts) {
      const applies = !d.room_ids || d.room_ids.length === 0 || d.room_ids.includes(id);
      if (applies && d.start_date <= refEnd && d.end_date >= refStart) {
        if (!best || d.percent > best.percent) best = d;
      }
    }
    return best;
  })();

  useEffect(() => {
    saveSearch({
      checkIn: checkIn ? format(checkIn, 'yyyy-MM-dd') : null,
      checkOut: checkOut ? format(checkOut, 'yyyy-MM-dd') : null,
      guests,
      children: childAges,
    });
  }, [checkIn, checkOut, guests, childAges]);

  useEffect(() => {
    if (!checkIn || !checkOut) { setPriceInfo(null); return; }
    setPriceLoading(true);
    axios.post(`${BACKEND_URL}/api/pricing/calculate`, {
      room_id: id,
      check_in: format(checkIn, 'yyyy-MM-dd'),
      check_out: format(checkOut, 'yyyy-MM-dd'),
      currency: 'TRY',
      guests: guests,
      children: childAges,
    }).then(res => { setPriceInfo(res.data); setPriceLoading(false); })
      .catch(() => { setPriceLoading(false); });
  }, [checkIn, checkOut, id, BACKEND_URL, guests, childAges.join(',')]);

  const nights = checkIn && checkOut
    ? Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 0;

  // Set page title
  useEffect(() => {
    if (room) {
      document.title = language === 'tr'
        ? `${room.name} | Kastamonu Amorium Hotel | 4 Mevsim Doğa Tatili`
        : `${room.name} | Kastamonu Amorium Hotel | 4 Seasons Nature Holiday`;
    }
  }, [room, language]);

  const handleBook = () => {
    if (!checkIn || !checkOut) { toast.error(t('dates_required')); return; }
    const params = new URLSearchParams({
      room_id: id,
      check_in: format(checkIn, 'yyyy-MM-dd'),
      check_out: format(checkOut, 'yyyy-MM-dd'),
      guests: String(guests),
    });
    if (childAges.length > 0) params.set('children', childAges.join(','));
    navigate(`/booking?${params.toString()}`);
  };

  const handleDateSelect = (d) => {
    if (activeField === 'checkin') {
      setCheckIn(d);
      if (checkOut && d >= checkOut) setCheckOut(null);
      setActiveField('checkout');
    } else if (activeField === 'checkout') {
      setCheckOut(d);
      setActiveField(null);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#F6F1E7' }}>
      <Navbar />
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        <Skeleton style={{ height: '320px', width: '100%', borderRadius: '16px', marginBottom: '24px' }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Skeleton style={{ height: '24px', width: '60%' }} />
          <Skeleton style={{ height: '24px', width: '40%' }} />
        </div>
      </div>
    </div>
  );

  if (!room) return null;

  const amenities = language === 'tr' ? room.amenities_tr : room.amenities_en;
  const images = room.images || [];
  const isDesktop = window.innerWidth >= 768;

  return (
    <div style={{ minHeight: '100vh', background: '#F6F1E7', fontFamily: 'Manrope, sans-serif' }}>
      <ActionBar />
      <Navbar />
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1.5rem 1.5rem 4rem' }}>

        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px', fontSize: '13px', color: '#6E7F5B' }}>
          <button onClick={() => navigate('/rooms')} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#6E7F5B', fontFamily: 'Manrope, sans-serif', fontSize: '13px' }}>
            <ChevronLeft size={14} /> {t('rooms')}
          </button>
          <span>/</span>
          <span style={{ color: '#1F3A2E', fontWeight: 600 }}>{room.name}</span>
        </div>

        {/* Two-column layout: left=content, right=booking card */}
        <div style={{ display: 'flex', gap: '40px', alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* LEFT COLUMN: Gallery + Info */}
          <div style={{ flex: '1 1 500px', minWidth: 0 }}>

            {/* Gallery - shows all images, click for lightbox */}
            <div style={{ borderRadius: '20px', overflow: 'hidden', marginBottom: '8px' }}>
              {images.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: images.length >= 3 ? '1fr 1fr' : '1fr',
                  gridTemplateRows: images.length >= 3 ? '200px 200px' : '400px',
                  gap: '4px',
                  height: images.length >= 3 ? '404px' : '400px',
                }}>
                  {/* Main big image - spans 2 rows */}
                  <div
                    onClick={() => { setGalleryIdx(0); setGalleryOpen(true); }}
                    style={{
                      gridRow: images.length >= 3 ? '1 / 3' : '1',
                      cursor: 'pointer', overflow: 'hidden', position: 'relative',
                      borderRadius: images.length >= 3 ? '16px 0 0 16px' : '16px',
                    }}
                  >
                    <img src={getImageUrl(images[0])} alt={room.name} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                      onMouseEnter={e => e.target.style.transform = 'scale(1.03)'}
                      onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                    />
                    <div style={{ position: 'absolute', bottom: '10px', left: '10px', background: 'rgba(0,0,0,0.5)', color: 'white', fontSize: '11px', padding: '3px 8px', borderRadius: '20px' }}>
                      1 / {images.length}
                    </div>
                  </div>
                  {/* Small images */}
                  {images.slice(1, 3).map((img, i) => (
                    <div key={i} onClick={() => { setGalleryIdx(i + 1); setGalleryOpen(true); }}
                      style={{ cursor: 'pointer', overflow: 'hidden', borderRadius: i === 0 ? '0 16px 0 0' : '0 0 16px 0' }}>
                      <img src={getImageUrl(img)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                        onMouseEnter={e => e.target.style.transform = 'scale(1.03)'}
                        onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Show all photos button */}
            <button
              onClick={() => { setGalleryIdx(0); setGalleryOpen(true); }}
              style={{ marginBottom: '24px', fontSize: '13px', color: '#1F3A2E', background: 'white', border: '1px solid #D8D1C6', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              📷 {language === 'tr' ? `${images.length} fotoğraf göster` : `Show all ${images.length} photos`}
            </button>

            {/* Room title + meta */}
            {detailDiscount && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: 'linear-gradient(135deg, #dc2626, #f97316)', color: 'white',
                borderRadius: '8px', padding: '6px 14px', fontSize: '13px', fontWeight: 800,
                boxShadow: '0 4px 14px rgba(220,38,38,0.4)', marginBottom: '10px',
                animation: 'flashPulse 2s infinite',
              }} data-testid="room-detail-discount-badge">
                🔥 {detailDiscount.name} — %{detailDiscount.percent} {language === 'tr' ? 'İNDİRİM' : 'OFF'}
              </div>
            )}
            <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '32px', fontWeight: 600, color: '#1F3A2E', marginBottom: '8px' }}>
              {room.name}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', fontSize: '14px', color: '#6E7F5B', marginBottom: '20px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Users size={15} /> {room.capacity} {t('guest_count')}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Maximize2 size={15} /> {room.size_m2}m²
              </span>
            </div>

            {/* Description */}
            <p style={{ fontSize: '15px', color: '#5A5450', lineHeight: 1.8, marginBottom: '28px' }}>
              {language === 'tr' ? room.description_tr : room.description_en}
            </p>

            {/* Amenities */}
            <div>
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '22px', fontWeight: 600, color: '#1F3A2E', marginBottom: '14px' }}>
                {t('amenities')}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                {(amenities || []).map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#3E5A3F' }}>
                    <CheckCircle2 size={15} style={{ color: '#B08D57', flexShrink: 0 }} />
                    {a}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Booking Card */}
          <div style={{ width: '340px', flexShrink: 0, position: 'sticky', top: '80px' }}>
            <div style={{ background: 'white', border: '1px solid #D8D1C6', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(31,58,46,0.1)' }}>
              {/* Price header */}
              <div style={{ padding: '20px 20px 14px', borderBottom: '1px solid #EFE7DA' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontSize: '26px', fontWeight: 800, color: '#1F3A2E', fontVariantNumeric: 'tabular-nums' }}>{formatPrice(room.base_price)}</span>
                    <span style={{ fontSize: '13px', color: '#6E7F5B', marginLeft: '4px' }}>{t('per_night')}</span>
                  </div>
                  <span style={{ fontSize: '12px', background: '#EFE7DA', color: '#3E5A3F', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>{room.size_m2}m²</span>
                </div>
              </div>

              {/* Date + Guest fields */}
              <div style={{ padding: '14px 20px' }}>
                {/* Check-in */}
                <button
                  onClick={() => setActiveField(activeField === 'checkin' ? null : 'checkin')}
                  style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '10px 12px', borderRadius: '10px', border: `1.5px solid ${activeField === 'checkin' ? '#1F3A2E' : '#D8D1C6'}`, background: activeField === 'checkin' ? '#F0FAF5' : 'white', cursor: 'pointer', marginBottom: '8px', fontFamily: 'Manrope, sans-serif' }}
                >
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '2px' }}>{t('check_in')}</span>
                  <span style={{ fontSize: '14px', fontWeight: checkIn ? 600 : 400, color: checkIn ? '#1F3A2E' : '#B0A99A' }}>
                    {checkIn ? format(checkIn, 'dd MMMM yyyy', { locale }) : (language === 'tr' ? 'Tarih seçin' : 'Select date')}
                  </span>
                </button>

                {/* Check-out */}
                <button
                  onClick={() => setActiveField(activeField === 'checkout' ? null : 'checkout')}
                  style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '10px 12px', borderRadius: '10px', border: `1.5px solid ${activeField === 'checkout' ? '#1F3A2E' : '#D8D1C6'}`, background: activeField === 'checkout' ? '#F0FAF5' : 'white', cursor: 'pointer', marginBottom: '8px', fontFamily: 'Manrope, sans-serif' }}
                >
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '2px' }}>{t('check_out')}</span>
                  <span style={{ fontSize: '14px', fontWeight: checkOut ? 600 : 400, color: checkOut ? '#1F3A2E' : '#B0A99A' }}>
                    {checkOut ? format(checkOut, 'dd MMMM yyyy', { locale }) : (language === 'tr' ? 'Tarih seçin' : 'Select date')}
                  </span>
                </button>

                {/* Inline Calendar */}
                {(activeField === 'checkin' || activeField === 'checkout') && (
                  <div style={{ background: '#F9F7F4', borderRadius: '12px', padding: '8px', marginBottom: '8px', border: '1px solid #EFE7DA' }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: '#1F3A2E', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px 4px' }}>
                      {activeField === 'checkin' ? (language === 'tr' ? '📅 Giriş Tarihi' : '📅 Check-in Date') : (language === 'tr' ? '📅 Çıkış Tarihi' : '📅 Check-out Date')}
                    </p>
                    <CustomCalendar
                      selected={activeField === 'checkin' ? checkIn : checkOut}
                      onSelect={handleDateSelect}
                      disabled={activeField === 'checkin' ? (d => { const t = new Date(); t.setHours(0,0,0,0); return d < t; }) : (d => d <= (checkIn || today))}
                      language={language}
                    />
                  </div>
                )}

                {/* Guests */}
                <button
                  onClick={() => setActiveField(activeField === 'guests' ? null : 'guests')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: '10px', border: `1.5px solid ${activeField === 'guests' ? '#1F3A2E' : '#D8D1C6'}`, background: activeField === 'guests' ? '#F0FAF5' : 'white', cursor: 'pointer', marginBottom: activeField === 'guests' ? '0' : '8px', fontFamily: 'Manrope, sans-serif' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '2px' }}>{t('guests')}</span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#1F3A2E' }}>
                      {guests} {language === 'tr' ? 'Yetişkin' : (guests > 1 ? 'Adults' : 'Adult')}
                      {childAges.length > 0 && `, ${childAges.length} ${language === 'tr' ? 'Çocuk' : (childAges.length > 1 ? 'Children' : 'Child')}`}
                    </span>
                  </div>
                  <Users size={16} style={{ color: '#B08D57' }} />
                </button>

                {/* Inline Guests */}
                {activeField === 'guests' && (
                  <div style={{ background: '#F9F7F4', borderRadius: '10px', padding: '10px', marginBottom: '8px', border: '1px solid #EFE7DA' }}>
                    <p style={{ fontSize: '10px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                      {language === 'tr' ? 'Yetişkin' : 'Adults'}
                    </p>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {Array.from({ length: room.capacity }, (_, i) => i + 1).map(n => (
                        <button key={n} onClick={() => setGuests(n)}
                          style={{ padding: '6px 14px', borderRadius: '20px', border: '1.5px solid', fontWeight: 600, fontSize: '13px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', background: guests === n ? '#1F3A2E' : 'white', color: guests === n ? 'white' : '#1F3A2E', borderColor: guests === n ? '#1F3A2E' : '#D8D1C6' }}>
                          {n}
                        </button>
                      ))}
                    </div>
                    <p style={{ fontSize: '10px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                      {language === 'tr' ? 'Çocuk' : 'Children'} ({language === 'tr' ? 'maks' : 'max'} {room.child_policy?.max_children ?? 2})
                    </p>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: childAges.length > 0 ? '10px' : '0' }}>
                      {Array.from({ length: (room.child_policy?.max_children ?? 2) + 1 }, (_, n) => (
                        <button key={n} onClick={() => setChildAges(prev => n > prev.length ? [...prev, ...Array(n - prev.length).fill(5)] : prev.slice(0, n))}
                          style={{ padding: '6px 14px', borderRadius: '20px', border: '1.5px solid', fontWeight: 600, fontSize: '13px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', background: childAges.length === n ? '#B08D57' : 'white', color: childAges.length === n ? 'white' : '#1F3A2E', borderColor: childAges.length === n ? '#B08D57' : '#D8D1C6' }}
                          data-testid={`room-detail-children-${n}`}>
                          {n}
                        </button>
                      ))}
                    </div>
                    {childAges.length > 0 && (
                      <>
                        <p style={{ fontSize: '10px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                          {language === 'tr' ? 'Çocuk Yaşları' : 'Child Ages'}
                        </p>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {childAges.map((age, i) => (
                            <select key={i} value={age}
                              onChange={e => setChildAges(prev => prev.map((a, j) => j === i ? parseInt(e.target.value) : a))}
                              style={{ padding: '6px 8px', borderRadius: '8px', border: '1.5px solid #D8D1C6', fontSize: '13px', fontFamily: 'Manrope, sans-serif', background: 'white', cursor: 'pointer' }}
                              data-testid={`room-detail-child-age-${i}`}>
                              {Array.from({ length: 12 }, (_, a) => (
                                <option key={a} value={a}>{`${a} ${language === 'tr' ? 'yaş' : 'yrs'}`}</option>
                              ))}
                            </select>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Price breakdown */}
                {priceInfo && nights > 0 && (
                  <div style={{ background: '#F6F1E7', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: '#6E7F5B' }}>
                        {language === 'tr' ? 'Oda Ücreti (KDV Hariç)' : 'Room Rate (Excl. VAT)'}
                      </span>
                      <span style={{ color: '#1F3A2E', fontWeight: 600 }}>{formatPrice(priceInfo.room_subtotal_try ?? priceInfo.subtotal_try)}</span>
                    </div>
                    {priceInfo.discount_total_try > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }} data-testid="room-detail-discount-row">
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>🔥 {language === 'tr' ? 'İndirim' : 'Discount'}</span>
                        <span style={{ color: '#16a34a', fontWeight: 700 }}>-{formatPrice(priceInfo.discount_total_try)}</span>
                      </div>
                    )}
                    {priceInfo.child_total_try > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                        <span style={{ color: '#6E7F5B' }}>{language === 'tr' ? 'Çocuk Ücreti' : 'Child Fee'}</span>
                        <span style={{ color: '#1F3A2E', fontWeight: 600 }}>{formatPrice(priceInfo.child_total_try)}</span>
                      </div>
                    )}
                    {priceInfo.daily_prices?.some(d => d.seasonal_multiplier > 1 || d.weekend_multiplier > 1) && (
                      <p style={{ fontSize: '11px', color: '#B08D57', marginTop: '2px', marginBottom: '4px' }}>
                        * {language === 'tr' ? 'Sezon/hafta sonu fiyatı uygulandı' : 'Season/weekend rate applied'}
                      </p>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px', marginTop: '4px' }}>
                      <span style={{ color: '#6E7F5B' }}>
                        KDV ({priceInfo.vat_rate}%)
                      </span>
                      <span style={{ color: '#1F3A2E', fontWeight: 600 }}>{formatPrice(priceInfo.vat_amount_try)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: '#6E7F5B' }}>
                        {language === 'tr' ? 'Konaklama Vergisi' : 'Accommodation Tax'} ({priceInfo.accommodation_tax_rate ?? 1}%)
                      </span>
                      <span style={{ color: '#1F3A2E', fontWeight: 600 }}>{formatPrice(priceInfo.accommodation_tax_amount_try || 0)}</span>
                    </div>
                    <div style={{ borderTop: '1px solid #D8D1C6', marginTop: '8px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '15px' }}>
                      <span style={{ color: '#1F3A2E' }}>
                        {language === 'tr' ? 'Toplam (Vergiler Dahil)' : 'Total (Incl. Taxes)'}
                      </span>
                      <span style={{ color: '#1F3A2E', fontVariantNumeric: 'tabular-nums' }}>{formatPrice(priceInfo.total_try)}</span>
                    </div>
                  </div>
                )}

                {priceLoading && (
                  <div style={{ background: '#F6F1E7', borderRadius: '10px', padding: '12px', marginBottom: '12px', height: '60px', animation: 'pulse 1.5s infinite' }} />
                )}

                {/* Book Button */}
                <button
                  onClick={handleBook}
                  style={{ width: '100%', background: '#1F3A2E', color: 'white', padding: '14px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: 700, fontFamily: 'Manrope, sans-serif', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#3E5A3F'}
                  onMouseLeave={e => e.currentTarget.style.background = '#1F3A2E'}
                >
                  {checkIn && checkOut ? t('book_now') : (language === 'tr' ? 'Tarih Seçin' : 'Select Dates')}
                </button>

                <p style={{ textAlign: 'center', fontSize: '12px', color: '#6E7F5B', marginTop: '10px' }}>
                  {language === 'tr' ? '🔒 Güvenli rezervasyon · Ücretsiz iptal imkânı' : '🔒 Secure booking · Free cancellation'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Gallery Lightbox - Full screen with thumbnail strip */}
      {galleryOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setGalleryOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          {/* Close */}
          <button onClick={() => setGalleryOpen(false)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', color: 'white', fontSize: '18px', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>

          {/* Counter */}
          <div style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.8)', fontSize: '13px', fontWeight: 600, background: 'rgba(0,0,0,0.4)', padding: '4px 14px', borderRadius: '20px', fontFamily: 'Manrope, sans-serif', whiteSpace: 'nowrap' }}>
            {room?.name} · {galleryIdx + 1} / {images.length}
          </div>

          {/* Main image */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', width: '100%' }}>
            <img src={getImageUrl(images[galleryIdx])} alt=""
              style={{ maxHeight: '70vh', maxWidth: '85vw', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
            />
          </div>

          {/* Left/Right arrows */}
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setGalleryIdx((galleryIdx - 1 + images.length) % images.length); }}
                style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '48px', height: '48px', cursor: 'pointer', color: 'white', fontSize: '22px', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ‹
              </button>
              <button onClick={(e) => { e.stopPropagation(); setGalleryIdx((galleryIdx + 1) % images.length); }}
                style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '48px', height: '48px', cursor: 'pointer', color: 'white', fontSize: '22px', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ›
              </button>
            </>
          )}

          {/* Thumbnail strip at bottom */}
          <div style={{ position: 'absolute', bottom: '16px', display: 'flex', gap: '8px', padding: '8px 16px', background: 'rgba(0,0,0,0.5)', borderRadius: '30px', maxWidth: '90vw', overflowX: 'auto' }}>
            {images.map((img, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setGalleryIdx(i); }}
                style={{ flexShrink: 0, width: '50px', height: '36px', borderRadius: '6px', overflow: 'hidden', cursor: 'pointer', border: i === galleryIdx ? '2.5px solid #B08D57' : '2.5px solid transparent', opacity: i === galleryIdx ? 1 : 0.5, transition: 'all 0.15s', background: 'transparent', padding: 0 }}>
                <img src={getImageUrl(img)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </button>
            ))}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
